"use client";

import React from "react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@rivval/css";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@rivval/ui/navigation-menu";

interface NavLink {
  href: Route;
  title: string;
  subNav?: NavLink[];
  isExternal?: boolean;
  description?: string;
  backgroundImage?: string;
}

interface NavItemsProps {
  navLinks?: NavLink[];
}

export function NavItems({ navLinks }: NavItemsProps) {
  const activePath: Route = usePathname();
  if (!navLinks || navLinks.length === 0) {
    return null;
  }

  return (
    <NavigationMenu>
      <NavigationMenuList>
        {navLinks.map((item, idx) => (
          <NavigationMenuItem key={`${item.href}-${idx}`}>
            {item.subNav ? (
              <>
                <NavigationMenuTrigger>{item.title}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-6 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                    {item.subNav.map((subItem, subIdx) =>
                      subIdx === 0 ? (
                        <li
                          className="row-span-3"
                          key={`${subItem.href}-${subIdx}`}
                        >
                          <NavigationMenuLink asChild>
                            <Link
                              className="group border-accent/10 from-muted/50 to-muted hover:border-muted-foreground relative flex h-full w-full flex-col justify-end overflow-hidden rounded-md border bg-linear-to-b p-4 no-underline outline-hidden transition-all duration-150 select-none focus:shadow-md"
                              href={subItem.href}
                            >
                              <div className="absolute inset-0 z-10 bg-black/10"></div>
                              {subItem.backgroundImage && (
                                <div
                                  className="absolute inset-0 bg-cover bg-center"
                                  style={{
                                    backgroundImage: `url('${subItem.backgroundImage}')`,
                                  }}
                                ></div>
                              )}
                              <div className="relative z-20">
                                <div className="font-heading mt-4 mb-2 text-lg text-white">
                                  {subItem.title}
                                  <span className="ml-1 opacity-0 transition-all group-hover:opacity-50">
                                    &rarr;
                                  </span>
                                </div>
                                <p className="text-sm leading-tight text-white">
                                  {subItem.description}
                                </p>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ) : (
                        <ListItem
                          key={`${subItem.href}-${subIdx}`}
                          title={subItem.title}
                          href={subItem.href}
                        >
                          {subItem.description}
                        </ListItem>
                      ),
                    )}
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <Link href={item.href} legacyBehavior passHref>
                <NavigationMenuLink
                  className={cn(
                    navigationMenuTriggerStyle(),
                    activePath === item.href && "text-primary",
                  )}
                >
                  {item.title}
                </NavigationMenuLink>
              </Link>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "group hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block space-y-1 rounded-md p-3 leading-none no-underline outline-hidden transition-colors select-none",
            className,
          )}
          {...props}
        >
          <div className="text-sm leading-none font-medium">
            <span className="font-heading">{title}</span>
            <span className="ml-1 opacity-0 transition-all group-hover:opacity-50">
              &rarr;
            </span>
          </div>
          <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";
